import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmptyOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddIcon from '@mui/icons-material/AddOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import { AddOtherDocumentsDialog } from '../../components/AddOtherDocumentsDialog';
import { DOCUMENT_ACCEPT, documentDisplayName, documentFileProblem } from '../../utils/documentNames';
import {
  getPortalDocumentRequirements,
  downloadPortalDocument,
  getPortalFinalDocuments,
  listPortalDocuments,
  uploadPortalDocument,
  viewPortalDocument,
} from '../../api/portal';
import { DocumentPreviewDialog, DocumentPreviewTarget } from '../../components/DocumentPreviewDialog';
import { downloadBlob } from '../../utils/downloadBlob';
import {
  PortalDocument,
  PortalDocumentChecklistItem,
  PortalDocumentRequirements,
  PortalDocumentSummary,
  PortalFinalDocumentItem,
  PortalOtherDocumentEntry,
} from '../../types/portal';

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong.') : 'Something went wrong.';

type RowStatus = PortalFinalDocumentItem['status'];

const STATUS: Record<RowStatus, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  NOT_APPLICABLE: { label: 'Not applicable', color: 'default' },
  MISSING: { label: 'Not uploaded', color: 'default' },
  PENDING: { label: 'Pending review', color: 'warning' },
  APPROVED: { label: 'Approved', color: 'success' },
  REJECTED: { label: 'Rejected', color: 'error' },
};

const statusIcon = (status: RowStatus) => {
  if (status === 'APPROVED') return <CheckCircleIcon color="success" fontSize="small" />;
  if (status === 'REJECTED') return <CancelIcon color="error" fontSize="small" />;
  if (status === 'PENDING') return <HourglassEmptyIcon color="warning" fontSize="small" />;
  if (status === 'NOT_APPLICABLE') return <RemoveCircleOutlineIcon color="disabled" fontSize="small" />;
  return <RadioButtonUncheckedIcon color="disabled" fontSize="small" />;
};

interface Row {
  key: string;
  label: string;
  detail?: string | null;
  referenceUrl?: string | null;
  status: RowStatus;
  file: { id: number; name: string; mimeType?: string; uploadedAt: string; version: number } | null;
  rejectionReason: string | null;
  /** Null when this row can't take an upload (approved, not applicable, or
   * no document type set up). */
  documentTypeId: number | null;
  /** An Other document's name (Phase 103) — its uploads replace that entry. */
  customName?: string;
  /** Earlier versions (Other documents), newest first. */
  history?: PortalDocumentSummary[];
}

/** Why there's nothing to show — never a bare "nothing configured". */
const NotConfiguredMessage = ({ requirements }: { requirements: PortalDocumentRequirements }) => {
  if (requirements.status === 'NO_PROGRAMME') {
    return (
      <Alert severity="info">
        Your document checklist isn't available yet because no programme has been assigned to you
        {requirements.university ? ` at ${requirements.university.name}` : ''}. Please contact your
        counselor — once your programme is recorded, its required documents will appear here.
      </Alert>
    );
  }
  return (
    <Alert severity="info">
      Document requirements have not been configured yet for {requirements.course?.name}
      {requirements.university ? ` at ${requirements.university.name}` : ''}. Please contact your
      counselor. An administrator can add them under Master Data → Academic Catalog →{' '}
      {requirements.course?.name} → Document Requirements.
    </Alert>
  );
};

/** Student Portal → Documents: the student's own checklists — Required and
 * Optional documents from their programme's Master Data requirements, then
 * the Final Documents List (the Final Documentation Checklist). Every
 * upload is an ordinary student document that lands as Pending review; only
 * staff approve or reject it. A rejected document shows the reason and can
 * be replaced; an approved or not-applicable one can't be uploaded again. */
export const PortalDocumentsTab = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFor, setUploadingFor] = useState<Row | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const requirementsQuery = useQuery({
    queryKey: ['portal', 'document-requirements'],
    queryFn: getPortalDocumentRequirements,
  });
  const finalDocumentsQuery = useQuery({ queryKey: ['portal', 'final-documents'], queryFn: getPortalFinalDocuments });
  const [showHistory, setShowHistory] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<DocumentPreviewTarget | null>(null);
  const documentsQuery = useQuery({
    queryKey: ['portal', 'documents', showHistory],
    queryFn: () => listPortalDocuments(showHistory),
  });

  const [progress, setProgress] = useState<number | null>(null);
  const [addOtherOpen, setAddOtherOpen] = useState(false);
  const [openHistory, setOpenHistory] = useState<Set<string>>(new Set());

  const uploadMutation = useMutation({
    mutationFn: ({ file, row }: { file: File; row: Row }) =>
      uploadPortalDocument(file, row.documentTypeId!, {
        onProgress: setProgress,
        ...(row.customName ? { customName: row.customName, newEntry: false } : {}),
      }),
    onMutate: () => {
      setProgress(0);
      setUploadError(null);
    },
    onSuccess: (_doc, { row }) => {
      setUploadError(null);
      setToast(`${row.label} uploaded — pending review.`);
    },
    onError: (err) => setUploadError(extractErrorMessage(err)),
    onSettled: () => {
      setUploadingFor(null);
      setProgress(null);
      queryClient.invalidateQueries({ queryKey: ['portal'] });
    },
  });
  const startUpload = (file: File, row: Row) => {
    const problem = documentFileProblem(file);
    if (problem) {
      setUploadingFor(null);
      setUploadError(problem);
      return;
    }
    uploadMutation.mutate({ file, row });
  };

  if (requirementsQuery.isLoading || finalDocumentsQuery.isLoading) {
    return (
      <Stack alignItems="center" spacing={1} sx={{ py: 6 }} role="status">
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Loading your documents…
        </Typography>
      </Stack>
    );
  }
  if (requirementsQuery.isError || !requirementsQuery.data) {
    return <Alert severity="error">Couldn't load your document checklist. Please try again later.</Alert>;
  }
  const requirements = requirementsQuery.data;
  // An API from before Phase 103 sends no otherDocuments.
  const finalDocuments = finalDocumentsQuery.data && {
    ...finalDocumentsQuery.data,
    otherDocuments: finalDocumentsQuery.data.otherDocuments ?? {
      number: '10',
      title: 'OTHER DOCUMENTS',
      documentType: null,
      entries: [],
    },
  };
  const documentsById = new Map<number, PortalDocument>((documentsQuery.data ?? []).map((d) => [d.id, d]));

  const download = async (file: { id: number; name: string }) => {
    try {
      downloadBlob(await downloadPortalDocument(file.id), file.name);
    } catch (err) {
      setUploadError(extractErrorMessage(err));
    }
  };
  const openView = (file: { id: number; name: string; mimeType?: string }) =>
    setPreviewTarget({
      key: file.id,
      originalName: file.name,
      mimeType: file.mimeType ?? 'application/pdf',
      fetchBlob: () => viewPortalDocument(file.id),
      onDownload: () => void download(file),
    });

  const programmeRow = (item: PortalDocumentChecklistItem): Row => {
    const uploaded = item.studentDocumentId ? documentsById.get(item.studentDocumentId) : undefined;
    return {
      key: `programme-${item.documentTypeId}`,
      label: item.documentTypeName,
      status: item.status,
      file: uploaded
        ? {
            id: uploaded.id,
            name: uploaded.originalName,
            mimeType: uploaded.mimeType,
            uploadedAt: uploaded.createdAt,
            version: uploaded.version,
          }
        : null,
      rejectionReason: item.rejectionReason,
      documentTypeId: item.status === 'APPROVED' ? null : item.documentTypeId,
    };
  };
  const finalRow = (item: PortalFinalDocumentItem): Row => ({
    key: `final-${item.key}`,
    label: `${item.letter ? `${item.letter}) ` : ''}${item.text}`,
    detail: item.documentType ? documentDisplayName(item.documentType.name) : null,
    referenceUrl: item.referenceUrl,
    status: item.status,
    file: item.document
      ? {
          id: item.document.id,
          name: item.document.originalName,
          uploadedAt: item.document.createdAt,
          version: item.document.version,
        }
      : null,
    rejectionReason: item.document?.rejectionReason ?? null,
    documentTypeId:
      item.documentType && item.status !== 'APPROVED' && item.status !== 'NOT_APPLICABLE'
        ? item.documentType.id
        : null,
  });

  const otherRow = (entry: PortalOtherDocumentEntry): Row => ({
    key: `other-${entry.name.toLowerCase()}`,
    label: entry.name,
    status: entry.status,
    file: {
      id: entry.document.id,
      name: entry.document.originalName,
      mimeType: entry.document.mimeType,
      uploadedAt: entry.document.createdAt,
      version: entry.document.version,
    },
    rejectionReason: entry.document.rejectionReason,
    documentTypeId:
      entry.status !== 'APPROVED' && finalDocuments?.otherDocuments.documentType
        ? finalDocuments.otherDocuments.documentType.id
        : null,
    customName: entry.name,
    history: entry.history,
  });

  const renderRow = (row: Row) => {
    const busy = uploadMutation.isPending && uploadingFor?.key === row.key;
    const action = row.status === 'MISSING' ? 'Upload' : row.customName ? 'Replace' : 'Re-upload';
    const historyOpen = openHistory.has(row.key);
    return (
      <Box key={row.key}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ py: 1, opacity: row.status === 'NOT_APPLICABLE' ? 0.6 : 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Box sx={{ pt: 0.25 }}>{statusIcon(row.status)}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">
              {row.label}
              {row.referenceUrl && (
                <>
                  {' '}
                  <Link href={row.referenceUrl} target="_blank" rel="noopener noreferrer">
                    {row.referenceUrl}
                  </Link>
                </>
              )}
            </Typography>
            {row.file && (
              <Typography variant="caption" color="text.secondary" display="block">
                {row.file.name}
                {row.file.version > 1 ? ` (version ${row.file.version})` : ''} · uploaded{' '}
                {new Date(row.file.uploadedAt).toLocaleDateString()}
              </Typography>
            )}
            {row.status === 'REJECTED' && (
              <Typography variant="caption" color="error" display="block">
                {row.rejectionReason ? `Reason: ${row.rejectionReason}. ` : ''}Please upload a replacement.
              </Typography>
            )}
          </Box>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ flexShrink: 0, '& .MuiButton-root': { whiteSpace: 'nowrap' } }}
        >
          <Chip size="small" label={STATUS[row.status].label} color={STATUS[row.status].color} />
          {row.file && (
            <Button
              size="small"
              startIcon={<VisibilityIcon />}
              aria-label={`View ${row.label}`}
              onClick={() => openView(row.file!)}
            >
              View
            </Button>
          )}
          {row.documentTypeId && (
            <Button
              size="small"
              startIcon={busy ? <CircularProgress size={14} /> : <UploadFileIcon />}
              disabled={uploadMutation.isPending}
              aria-label={`${action} ${row.label}`}
              onClick={() => {
                setUploadingFor(row);
                fileInputRef.current?.click();
              }}
            >
              {action}
            </Button>
          )}
        </Stack>
      </Stack>
      {busy && progress !== null && (
        <LinearProgress
          variant="determinate"
          value={progress}
          aria-label={`Uploading ${row.label}`}
          sx={{ mb: 1, borderRadius: 1 }}
        />
      )}
      {row.history && row.history.length > 0 && (
        <>
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            sx={{ ml: 3 }}
            onClick={() =>
              setOpenHistory((prev) => {
                const next = new Set(prev);
                if (next.has(row.key)) next.delete(row.key);
                else next.add(row.key);
                return next;
              })
            }
          >
            {historyOpen ? 'Hide' : 'Show'} earlier versions ({row.history.length})
          </Button>
          {historyOpen && (
            <Stack spacing={0.5} sx={{ ml: 4, pl: 1.5, mb: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
              {row.history.map((h) => (
                <Stack key={h.id} direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" color="text.secondary">
                    {h.originalName} · version {h.version} · uploaded {new Date(h.createdAt).toLocaleDateString()}
                    {h.verificationStatus === 'REJECTED' && h.rejectionReason ? ` · rejected: ${h.rejectionReason}` : ''}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    aria-label={`View ${row.label} version ${h.version}`}
                    onClick={() => openView({ id: h.id, name: h.originalName, mimeType: h.mimeType })}
                  >
                    View
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </>
      )}
      </Box>
    );
  };
  // Which document types the student may upload against right now (applicable,
  // not approved) — "Replace" on a document uses the same rules.
  const uploadableByType = new Map<number, Row>();
  const otherRows = finalDocuments?.otherDocuments.entries.map(otherRow) ?? [];
  const uploadableOther = new Map(
    otherRows.filter((r) => r.documentTypeId).map((r) => [r.customName!.toLowerCase(), r]),
  );
  for (const row of [
    ...(requirements.status === 'CONFIGURED' ? [...requirements.required, ...requirements.optional].map(programmeRow) : []),
    ...(finalDocuments?.sections.flatMap((s) => s.items).map(finalRow) ?? []),
  ]) {
    if (row.documentTypeId) uploadableByType.set(row.documentTypeId, row);
  }

  const rowList = (rows: Row[]) => (
    <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>{rows.map(renderRow)}</Stack>
  );

  return (
    <Stack spacing={2}>
      {uploadError && (
        <Alert severity="error" onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={DOCUMENT_ACCEPT}
        data-testid="portal-document-file"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && uploadingFor) startUpload(file, uploadingFor);
          e.target.value = '';
        }}
      />

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }} data-testid="portal-required-documents">
        <Typography variant="subtitle1" fontWeight={600}>
          Required documents
        </Typography>
        {requirements.course && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {requirements.course.name}
            {requirements.university ? ` — ${requirements.university.name}` : ''}
          </Typography>
        )}
        {requirements.status !== 'CONFIGURED' ? (
          <NotConfiguredMessage requirements={requirements} />
        ) : (
          <>
            {requirements.summary && requirements.summary.requiredTotal > 0 && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                {requirements.summary.approved} of {requirements.summary.requiredTotal} required documents approved
                {requirements.summary.pendingApproval > 0 && ` · ${requirements.summary.pendingApproval} pending review`}
                {requirements.summary.missing > 0 && ` · ${requirements.summary.missing} not uploaded`}
                {requirements.summary.rejected > 0 && ` · ${requirements.summary.rejected} rejected`}
              </Typography>
            )}
            {rowList(requirements.required.map(programmeRow))}
            {requirements.required.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No mandatory documents are required for your programme.
              </Typography>
            )}
            {requirements.optional.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Optional documents
                </Typography>
                {rowList(requirements.optional.map(programmeRow))}
              </>
            )}
          </>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }} data-testid="portal-final-documents">
        <Typography variant="subtitle1" fontWeight={600}>
          Final Documents List
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The final documents needed before departure. Each one is checked by IMC after you upload it.
        </Typography>
        {finalDocumentsQuery.isError || !finalDocuments ? (
          <Alert severity="error">Couldn't load your Final Documents List. Please try again later.</Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {finalDocuments.progress.approved} of {finalDocuments.progress.total} final documents approved
              {finalDocuments.progress.pending > 0 && ` · ${finalDocuments.progress.pending} pending review`}
              {finalDocuments.progress.missing > 0 && ` · ${finalDocuments.progress.missing} not uploaded`}
              {finalDocuments.progress.rejected > 0 && ` · ${finalDocuments.progress.rejected} rejected`}
            </Typography>
            {finalDocuments.progress.total > 0 && (
              <LinearProgress
                variant="determinate"
                value={Math.round((finalDocuments.progress.approved / finalDocuments.progress.total) * 100)}
                color={finalDocuments.progress.complete ? 'success' : 'primary'}
                aria-label="Final documents approved"
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
              />
            )}
            {finalDocuments.sections.map((section) => (
              <Box key={section.number} sx={{ mt: 1.5 }}>
                <Typography variant="subtitle2">
                  {section.number}. {section.title}
                  {section.note && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({section.note})
                    </Typography>
                  )}
                </Typography>
                {rowList(section.items.map(finalRow))}
              </Box>
            ))}
            <Box sx={{ mt: 2.5 }} data-testid="portal-other-documents">
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
              >
                <Box>
                  <Typography variant="subtitle2">
                    {finalDocuments.otherDocuments.number}. {finalDocuments.otherDocuments.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Anything else IMC has asked you for, each under its own name.
                  </Typography>
                </Box>
                {finalDocuments.otherDocuments.documentType && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setAddOtherOpen(true)}
                  >
                    Add documents
                  </Button>
                )}
              </Stack>
              {otherRows.length > 0 ? (
                rowList(otherRows)
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  {finalDocuments.otherDocuments.documentType
                    ? 'No other documents added yet.'
                    : 'Other documents are not available yet. Please contact your counselor.'}
                </Typography>
              )}
            </Box>
          </>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }} data-testid="portal-all-documents">
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={600}>
          All my documents
        </Typography>
          <FormControlLabel
            control={<Switch size="small" checked={showHistory} onChange={(e) => setShowHistory(e.target.checked)} />}
            label="Show earlier versions"
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Everything on your record — uploaded by you or by IMC — with its review status.
        </Typography>
        {documentsQuery.isError ? (
          <Alert severity="error">Couldn't load your documents. Please try again later.</Alert>
        ) : documentsQuery.isLoading ? (
          <CircularProgress size={20} />
        ) : (documentsQuery.data ?? []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No documents have been uploaded yet.
          </Typography>
        ) : (
          <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
            {(documentsQuery.data ?? []).map((d) => {
              const status = d.verificationStatus;
              const label = d.name ?? documentDisplayName(d.documentType.name, d.customName);
              const replaceRow =
                d.isLatest && status !== 'APPROVED'
                  ? d.customName
                    ? uploadableOther.get(d.customName.toLowerCase())
                    : uploadableByType.get(d.documentType.id)
                  : undefined;
              return (
                <Stack
                  key={d.id}
                  data-testid={`portal-document-${d.id}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  sx={{ py: 1, opacity: d.isLatest ? 1 : 0.75 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2">{label}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {d.originalName}
                      {d.version > 1 || !d.isLatest ? ` · version ${d.version}${d.isLatest ? '' : ' (replaced)'}` : ''} ·
                      uploaded {new Date(d.createdAt).toLocaleDateString()}
                      {d.uploadedBy ? ` by ${d.uploadedBy === 'YOU' ? 'you' : 'IMC'}` : ''}
                      {d.verifiedAt && status !== 'PENDING' ? ` · reviewed ${new Date(d.verifiedAt).toLocaleDateString()}` : ''}
                    </Typography>
                    {status === 'REJECTED' && d.rejectionReason && (
                      <Typography variant="caption" color="error" display="block">
                        Reason: {d.rejectionReason}
                        {d.isLatest ? '. Please upload a replacement.' : ''}
                      </Typography>
                    )}
                  </Box>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ flexShrink: 0, '& .MuiButton-root': { whiteSpace: 'nowrap' } }}
                  >
                    <Chip size="small" label={STATUS[status].label} color={STATUS[status].color} />
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      aria-label={`View ${d.originalName}${d.isLatest ? '' : ` version ${d.version}`}`}
                      onClick={() => openView({ id: d.id, name: d.originalName, mimeType: d.mimeType })}
                    >
                      View
                    </Button>
                    <IconButton
                      size="small"
                      aria-label={`Download ${d.originalName}${d.isLatest ? '' : ` version ${d.version}`}`}
                      onClick={() => void download({ id: d.id, name: d.originalName })}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    {replaceRow && (
                      <Button
                        size="small"
                        startIcon={<UploadFileIcon />}
                        disabled={uploadMutation.isPending}
                        aria-label={`Replace ${label}`}
                        onClick={() => {
                          setUploadingFor(replaceRow);
                          fileInputRef.current?.click();
                        }}
                      >
                        Replace
                      </Button>
                    )}
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Paper>

      {finalDocuments?.otherDocuments.documentType && (
        <AddOtherDocumentsDialog
          open={addOtherOpen}
          existingNames={finalDocuments.otherDocuments.entries.map((e) => e.name)}
          onClose={() => setAddOtherOpen(false)}
          upload={(name, file, onProgress) =>
            uploadPortalDocument(file, finalDocuments.otherDocuments.documentType!.id, {
              customName: name,
              newEntry: true,
              onProgress,
            })
          }
          onUploaded={(names) => {
            setToast(
              names.length === 1
                ? `${names[0]} uploaded — pending review.`
                : `${names.length} documents uploaded — pending review.`,
            );
            queryClient.invalidateQueries({ queryKey: ['portal'] });
          }}
        />
      )}
      <DocumentPreviewDialog target={previewTarget} onClose={() => setPreviewTarget(null)} />
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity="success" onClose={() => setToast(null)}>
            {toast}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
};
